'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

const ANALYTICS_IDLE_DELAY_MS = 15000;

type GtagWindow = Window & {
  dataLayer?: Array<Record<string, unknown> | unknown[] | IArguments>;
  gtag?: (...args: unknown[]) => void;
};

/**
 * The gtag shim. It MUST push the `arguments` object, never an array.
 *
 * gtag.js only executes commands it receives as an Arguments object (Google's own
 * snippet is `function gtag(){dataLayer.push(arguments);}`). A rest-parameter version
 * (`(...args) => dataLayer.push(args)`) pushes a plain Array, which gtag.js silently
 * ignores: `js`, `config` and every `event` became no-ops, so NO tag ever sent a hit.
 * Found 2026-09-25 when the Google Ads tag (AW-18391334302) showed "No recent data"
 * two days after going live: the data layer held `[object Array]` entries, and pushing
 * the same commands as `arguments` immediately fired the remarketing hits. Do not
 * "tidy" this into rest params.
 */
export function makeGtag(w: GtagWindow): (...args: unknown[]) => void {
  return function gtag() {
    w.dataLayer = w.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params -- gtag.js requires the Arguments object
    w.dataLayer.push(arguments);
  };
}

// Lazy-load the monitoring bootstrap so it never blocks first paint
const AnalyticsInitInner = dynamic(
  () => import('@/lib/monitoring/unified').then(() => ({ default: () => null })),
  { ssr: false }
);

/**
 * Bootstraps Mixpanel + PostHog after the page is interactive.
 * Deferred so analytics never competes with the LCP element.
 */
export function AnalyticsInit() {
  const [mounted, setMounted] = useState(false);
  const ga4MeasurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const googleAdsTagId = process.env.NEXT_PUBLIC_GOOGLE_ADS_TAG_ID;

  useEffect(() => {
    let timer: number | undefined;
    let cancelled = false;
    let started = false;

    const boot = () => {
      if (cancelled || started) return;
      started = true;

      if (timer) window.clearTimeout(timer);
      window.removeEventListener('pointerdown', boot);
      window.removeEventListener('keydown', boot);
      window.setTimeout(() => setMounted(true), 0);
    };

    const schedule = () => {
      timer = window.setTimeout(boot, ANALYTICS_IDLE_DELAY_MS);
      window.addEventListener('pointerdown', boot, { once: true, passive: true });
      window.addEventListener('keydown', boot, { once: true });
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      cancelled = true;

      if (timer) window.clearTimeout(timer);
      window.removeEventListener('load', schedule);
      window.removeEventListener('pointerdown', boot);
      window.removeEventListener('keydown', boot);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // NOTE: there is deliberately no bot gate here. GA4/GTM must load for
    // crawlers so Googlebot renders the page as a user would. This block used
    // to compute an `isBot` flag and never use it, which read as "bot traffic
    // is handled" when nothing was gated on it. Bot suppression happens where
    // it actually belongs: Mixpanel ($ignore, lib/monitoring/unified.ts) and
    // the event_log is_bot marker (lib/queue/event-worker.ts).

    // SAFETY: The surrounding typed contract establishes the asserted representation.
    const gtmWindow = window as GtagWindow;

    if (gtmId && !document.getElementById('gtm-script')) {
      gtmWindow.dataLayer = gtmWindow.dataLayer || [];
      gtmWindow.dataLayer.push({
        'gtm.start': Date.now(),
        event: 'gtm.js',
      });

      const gtmScript = document.createElement('script');
      gtmScript.id = 'gtm-script';
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}&l=dataLayer`;
      document.head.appendChild(gtmScript);
    }

    if (ga4MeasurementId && !document.getElementById('ga4-script')) {
      const gaScript = document.createElement('script');
      gaScript.id = 'ga4-script';
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`;
      document.head.appendChild(gaScript);

      const gtag = makeGtag(gtmWindow);
      // Expose gtag so TrackPageView can fire client-side page_views (GA4 sessions
      // + channel attribution come from these real browser hits, not server MP).
      gtmWindow.gtag = gtag;

      gtag('js', new Date());
      // send_page_view:false — gtag's own auto page_view only fires on hard loads, not
      // SPA route changes. TrackPageView fires an explicit gtag page_view on EVERY route
      // (including the first), so this config just establishes the session + captures the
      // acquisition source/referrer. Setting true would double-count the first load.
      gtag('config', ga4MeasurementId, { send_page_view: false });
    }

    // Google Ads tag — remarketing audiences ONLY (no conversion pixels; conversions
    // upload server-side from the ad_conversions outbox). Without this the audience
    // list cannot grow: on 2026-09-23 the account's one list held 64 users against
    // Google's ~1,000 minimum to serve, because nothing was ever counting visitors.
    // gtag.js serves every Google destination, so when GA4 already injected it we add
    // a second `config` instead of a second script.
    if (googleAdsTagId && !document.getElementById('google-ads-tag-configured')) {
      const adsGtag = makeGtag(gtmWindow);

      if (!document.getElementById('ga4-script')) {
        const adsScript = document.createElement('script');
        adsScript.id = 'google-ads-script';
        adsScript.async = true;
        adsScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsTagId}`;
        document.head.appendChild(adsScript);
        gtmWindow.gtag = gtmWindow.gtag ?? adsGtag;
        (gtmWindow.gtag ?? adsGtag)('js', new Date());
      }

      (gtmWindow.gtag ?? adsGtag)('config', googleAdsTagId);
      // Marker element, not a boolean: the effect re-runs on every dependency change
      // and a second `config` for the same id would double-count the visitor.
      const marker = document.createElement('meta');
      marker.id = 'google-ads-tag-configured';
      document.head.appendChild(marker);
    }
  }, [ga4MeasurementId, gtmId, googleAdsTagId, mounted]);

  if (!mounted) return null;

  return <AnalyticsInitInner />;
}
